import React, { useEffect, useRef, useState } from 'react';
import { BookmarkCheck, Bookmark } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { getUserPreference, setUserPreference } from 'core/api/apiService';

/**
 * AutoFilterToggle — "Filter merken": speichert den aktuellen Filter pro User & Sicht
 * (bestehende user_preferences-Infra, prefKey) und stellt ihn beim nächsten Login
 * automatisch wieder her. An → aktueller Filter wird (debounced) persistiert.
 * Aus → gespeicherter Filter wird gelöscht (value=null).
 *
 * @param {Object} user
 * @param {string} prefKey        eindeutiger Key [a-z0-9_-]{1,60}, z.B. 'lager-auto-filter'
 * @param {Object} currentFilter  serialisierbarer aktueller Filter-State
 * @param {(saved:Object)=>void} onRestore  wendet einen geladenen Filter an
 */
const AutoFilterToggle = ({ user, prefKey, currentFilter, onRestore }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [remember, setRemember] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;
  const timerRef = useRef(null);       // laufender Debounce-Timer (abbrechbar)
  const lastSavedRef = useRef(null);   // zuletzt gespeicherter Stand → keine redundanten PUTs

  const save = (value) => {
    setUserPreference(user, prefKey, value).catch((e) => console.warn('[AutoFilterToggle] persist failed', e));
  };

  // Mount: gespeicherten Filter laden + anwenden
  useEffect(() => {
    let cancelled = false;
    getUserPreference(user, prefKey)
      .then((res) => {
        if (cancelled) return;
        if (res && res.value && typeof res.value === 'object') {
          lastSavedRef.current = JSON.stringify(res.value);
          setRemember(true);
          onRestoreRef.current(res.value);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [user, prefKey]);

  const serialized = JSON.stringify(currentFilter || {});

  // Persist-on-change (debounced), wenn "merken" aktiv — überspringt unveränderten Stand
  useEffect(() => {
    if (!loaded || !remember) return undefined;
    if (serialized === lastSavedRef.current) return undefined;
    timerRef.current = setTimeout(() => {
      lastSavedRef.current = serialized;
      save(JSON.parse(serialized));
    }, 600);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [serialized, remember, loaded, user, prefKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => {
    const next = !remember;
    setRemember(next); // reiner State-Update
    if (timerRef.current) clearTimeout(timerRef.current); // laufenden Save abbrechen (kein Race mit Delete)
    if (next) {
      lastSavedRef.current = serialized;
      save(JSON.parse(serialized));
    } else {
      lastSavedRef.current = null;
      save(null);
    }
  };

  const chipBase = 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer select-none';
  const active = isDarkMode ? 'bg-brand/25 border-brand/50 text-blue-200' : 'bg-brand/10 border-brand/40 text-brand';
  const inactive = isDarkMode ? 'bg-white/8 border-white/15 text-white/60 hover:bg-white/12' : 'bg-white border-slate-200 text-slate-600 hover:border-brand/30 hover:text-brand';

  return (
    <button type="button" onClick={toggle} className={`${chipBase} ${remember ? active : inactive}`} title={t('lm.rememberFilterHint')}>
      {remember ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
      <span>{t('lm.rememberFilter')}</span>
    </button>
  );
};

export default AutoFilterToggle;
